'use client'

import { Box } from '@mantine/core'
import { Suspense } from 'react'

import { useCoinContext } from '@/app/[locale]/price/[slug]/_contexts/CoinContext'
import FaqSectionTemplate from '@/app/[locale]/price/stocks/[symbol]/_components/StockContentSections/FaqSection'
import { SectionErrorBoundary } from '@/components/error/boundary'
import FaqSchema from '@/components/schema/FaqSchema'
import { useStocksContent } from '@/services/stock/content'
import { assetDetailToMeta, useStockBySymbol } from '@/services/stock/meta'
import type { StockAssetDetail, StockOverall } from '@/types/stock'
import { lexicalToPlainText } from '@/utils/richTextToPlainText'
import { replaceStockPlaceholders } from '@/utils/stockPlaceholderReplacer'

const assetDetailToStockOverall = (asset: StockAssetDetail): StockOverall =>
  ({
    ...assetDetailToMeta(asset),
    usd_price: parseFloat(asset.previous_closing_price ?? '0'),
    usd_marketcap: parseFloat(asset.market_cap ?? '0'),
    usd_52_week_high: parseFloat(asset.week52_high_price ?? '0'),
    usd_52_week_low: parseFloat(asset.week52_low_price ?? '0'),
    usd_dividend_yield: parseFloat(asset.dividend_yield ?? '0'),
    usd_dividend_per_share: parseFloat(asset.dividend_price ?? '0'),
  }) as unknown as StockOverall

function FaqSectionContent() {
  const { coin } = useCoinContext()
  const { data: asset } = useStockBySymbol(coin.symbol)
  const { data: content } = useStocksContent(asset?.slug.toLowerCase() ?? '')

  if (!content?.faq?.length || !asset) return null
  const stock = assetDetailToStockOverall(asset)

  const faqSchemaData =
    content.faq?.map(item => ({
      question: replaceStockPlaceholders(item.faq.question, stock),
      answer: replaceStockPlaceholders(lexicalToPlainText(item.faq.answer), stock),
    })) ?? []

  return (
    <Box mt={{ base: 40, md: 80 }}>
      {faqSchemaData.length > 0 && <FaqSchema faqs={faqSchemaData} />}
      <FaqSectionTemplate content={content} stockName={asset.name ?? asset.symbol} stock={stock} />
    </Box>
  )
}

function FaqSection() {
  return (
    <Suspense fallback={null}>
      <FaqSectionContent />
    </Suspense>
  )
}

export default SectionErrorBoundary.with(FaqSection)
